/**
 * Self-hosted session helpers (Keycloak JWT via API login).
 */

import axios from 'axios';
import {
  AUTH_REFRESH_TOKEN_KEY,
  AUTH_SESSION_EVENT,
  AUTH_TOKEN_EXPIRES_KEY,
  AUTH_TOKEN_KEY,
  AUTH_USER_KEY,
  StoredAuthUser,
  isSelfHosted,
} from '../config/infra';
import { SERVER_API_BASE } from '../config/apiBase';

export interface SelfHostedLoginResult {
  token: string;
  user: StoredAuthUser;
}

const TOKEN_REFRESH_SKEW_MS = 60_000;

function persistSession(
  data: {
    id_token: string;
    refresh_token?: string | null;
    expires_in?: number | null;
    uid: string;
    email?: string | null;
    display_name?: string | null;
    organization_id?: string | null;
    custom_claims?: { role?: string | null };
  }
): SelfHostedLoginResult {
  const user: StoredAuthUser = {
    uid: data.uid,
    email: data.email,
    displayName: data.display_name,
    organizationId: data.organization_id,
    role: data.custom_claims?.role ?? null,
  };
  localStorage.setItem(AUTH_TOKEN_KEY, data.id_token);
  if (data.refresh_token) {
    localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, data.refresh_token);
  }
  if (typeof data.expires_in === 'number' && data.expires_in > 0) {
    localStorage.setItem(
      AUTH_TOKEN_EXPIRES_KEY,
      String(Date.now() + data.expires_in * 1000)
    );
  }
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
  window.dispatchEvent(new Event(AUTH_SESSION_EVENT));
  return { token: data.id_token, user };
}

export async function selfHostedLogin(
  email: string,
  password: string
): Promise<SelfHostedLoginResult> {
  const response = await axios.post(`${SERVER_API_BASE}/api/v1/auth/login`, {
    email,
    password,
  });
  return persistSession(response.data);
}

let refreshInFlight: Promise<string | null> | null = null;

export async function refreshSelfHostedToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem(AUTH_REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    return getSelfHostedToken();
  }

  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const response = await axios.post(`${SERVER_API_BASE}/api/v1/auth/refresh`, {
          refresh_token: refreshToken,
        });
        const result = persistSession(response.data);
        return result.token;
      } catch (error) {
        const status = axios.isAxiosError(error)
          ? error.response?.status
          : undefined;
        if (status === 401 || status === 400) {
          clearSelfHostedSession();
        }
        return null;
      } finally {
        refreshInFlight = null;
      }
    })();
  }

  return refreshInFlight;
}

export async function getValidSelfHostedToken(): Promise<string | null> {
  const token = getSelfHostedToken();
  const refreshToken = localStorage.getItem(AUTH_REFRESH_TOKEN_KEY);
  const expiresRaw = localStorage.getItem(AUTH_TOKEN_EXPIRES_KEY);
  const expiresAt = expiresRaw ? Number(expiresRaw) : NaN;

  const tokenFresh =
    Boolean(token) &&
    Number.isFinite(expiresAt) &&
    expiresAt - Date.now() > TOKEN_REFRESH_SKEW_MS;

  if (tokenFresh) {
    return token;
  }

  if (refreshToken) {
    return refreshSelfHostedToken();
  }

  // Legacy sessions without expiry metadata: keep using the stored token until the API rejects it.
  if (token && !Number.isFinite(expiresAt)) {
    return token;
  }

  return null;
}

export function getSelfHostedToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function getSelfHostedUser(): StoredAuthUser | null {
  const raw = localStorage.getItem(AUTH_USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredAuthUser;
  } catch {
    return null;
  }
}

export function clearSelfHostedSession(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY);
  localStorage.removeItem(AUTH_TOKEN_EXPIRES_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
  window.dispatchEvent(new Event(AUTH_SESSION_EVENT));
}

export function isSelfHostedMode(): boolean {
  return isSelfHosted;
}
